import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useChatbotOffset } from '@/hooks/useChatbotOffset';

const ScrollToTopButton = () => {
  const [show, setShow] = useState(false);
  const chatbotOffset = useChatbotOffset();

  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!show) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      style={{ position: 'fixed', bottom: chatbotOffset, right: '24px', zIndex: 9999 }}
      className="w-10 h-10 bg-[#89B56B] text-white rounded shadow-lg hover:bg-[#5DAA68] transition-colors flex items-center justify-center cursor-pointer group"
      aria-label="ページトップへ"
    >
      <i className="ri-arrow-up-line text-lg group-hover:-translate-y-0.5 transition-transform"></i>
    </button>
  );
};

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isRecruitPage = location.pathname === '/recruit';

  const footerColumns = [
    {
      title: '会社概要',
      links: [
        { title: '会社情報', href: '/company' },
        { title: '経営指針', href: '/policy' },
        { title: '情報セキュリティ基本方針', href: '/security' },
        { title: '健康企業宣言', href: '/health' },
        { title: '社長インタビュー', href: '/interview' },
      ],
    },
    {
      title: '事業案内',
      links: [
        { title: '事業案内', href: '/service' },
        { title: '開発実績', href: '/record' },
      ],
    },
  ];

  const rightColumns = [
    { title: 'プロダクト', href: '/products' },
    { title: '採用情報', href: '/recruit' },
    { title: 'お問い合わせ', href: '/contact' },
    { title: 'アクセス', href: '/access' },
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    navigate(href);
  };

  return (
    <>
      <footer className="bg-[#89B56B] pt-12 pb-0">
        <div className="max-w-7xl mx-auto px-6">
          {/* PC版: grid layout */}
          <div className="hidden md:grid grid-cols-4 gap-6">
            {footerColumns.map((column, index) => (
              <div key={index}>
                <h3 className="text-sm font-bold text-white pb-2 border-b border-white/60 mb-3">
                  {column.title}
                </h3>
                {column.links.length > 0 && (
                  <ul className="space-y-1.5">
                    {column.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <a
                          href={link.href}
                          onClick={(e) => handleLinkClick(e, link.href)}
                          className="text-xs text-white/80 hover:text-white transition-colors cursor-pointer"
                        >
                          {link.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <div className="col-span-2">
              <div className="flex gap-8">
                {rightColumns.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    onClick={(e) => handleLinkClick(e, item.href)}
                    className="inline-block text-sm font-bold text-white pb-2 border-b border-white/60 cursor-pointer hover:text-white/80 transition-colors"
                  >
                    {item.title}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* スマホ版: 縦並び */}
          <div className="flex flex-col gap-6 md:hidden">
            {footerColumns.map((column, index) => (
              <div key={index}>
                <h3 className="text-sm font-bold text-white pb-2 border-b border-white/60 mb-3">
                  {column.title}
                </h3>
                {column.links.length > 0 && (
                  <ul className="space-y-1.5">
                    {column.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <a
                          href={link.href}
                          onClick={(e) => handleLinkClick(e, link.href)}
                          className="text-xs text-white/80 hover:text-white transition-colors cursor-pointer"
                        >
                          {link.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {rightColumns.map((item, index) => (
              <div key={index}>
                <a
                  href={item.href}
                  onClick={(e) => handleLinkClick(e, item.href)}
                  className="inline-block text-sm font-bold text-white pb-2 border-b border-white/60 cursor-pointer hover:text-white/80 transition-colors w-full"
                >
                  {item.title}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* コピーライト */}
        <div className="mt-10 py-4 border-t border-white/30 bg-white">
          <div className="text-center">
            <p className="text-xs text-[#89B56B]">
              Copyright &copy; {new Date().getFullYear()} 株式会社MSK All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      {!isRecruitPage && <ScrollToTopButton />}
    </>
  );
};

export default Footer;
